// deps
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import ElementObserver from "@swiggy-private/dls-react-mxn/dist/ElementObserver";
import {
    AnalyticsContext,
    TableContext,
    ResponseContext,
    useDigitalMenuConfig,
} from "@swiggy-private/portal-dineout-context";
import { CurtainRaiserUI, type ICurtainRaiserProps } from "@swiggy-private/portal-dineout-ui";
import { isCurrentUserHost } from "utils";
import { CURTAIN_RAISER_FREQUENCY_FLAG_TIMEOFFSET_MS } from "./constants";
import CurtainError from "./CurtainError";
import { sendPageImpressionEvent, sendSwipeUpEvent } from "./helpers";
import { CurtainRaiserContainer } from "./styled";
import { CurtainRaiserProps } from "./types";
import { STORAGE_KEYS } from "../../constants/StorageKeys";
import { isFrequencyCappedWithTableId, updateFrequencyCappingWithTableId } from "../../utils/frequencyCapping";
import { getCurtainRaiserProps } from "../../utils/widgets";

const CurtainRaiser = ({
    show,
    setShow,
    ready,
    setReady,
    setIsCurtainRaiserShown,
    setHasBottomBar,
}: CurtainRaiserProps) => {
    const [data, setData] = useState<ICurtainRaiserProps>({} as ICurtainRaiserProps);
    const { state } = useContext(ResponseContext);
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const tableContext = useContext(TableContext);
    const { tableId = "", restaurantId = "" } = tableContext;
    const { config } = useDigitalMenuConfig();
    const [swipeUp, setSwipeUp] = useState(false);
    const [showError, setShowError] = useState(false);
    const isUserHost = isCurrentUserHost(state.pin?.data?.status ?? "");
    const overflowModifiedRef = useRef(false);
    const previousOverflowRef = useRef("");
    const hasProcessedRef = useRef(false);
    // Prefetched data from useMenuDataPrefetch (runs parallel to config API)
    const prefetchedData = state.curtain?.data;
    const isPrefetchLoading = state.curtain?.loading;
    const hasPrefetchError = state.curtain?.error;

    const handleSwipeUp = useCallback(() => {
        sendSwipeUpEvent({
            eventFn: clickEvent,
            tableContext,
            restaurantName: data.restaurantName,
            isUserHost,
            config,
        });

        updateFrequencyCappingWithTableId(STORAGE_KEYS.CURTAIN_RAISER, tableId as string);
        setSwipeUp(true);
        setIsCurtainRaiserShown(true);
        if (overflowModifiedRef.current) {
            document.body.style.overflow = previousOverflowRef.current || "";
            overflowModifiedRef.current = false;
        }
        setTimeout(() => {
            setShow(false);
        }, 1000);
    }, [clickEvent, tableContext, data.restaurantName, isUserHost, config, tableId, setIsCurtainRaiserShown, setShow]);

    const onPageImpression = useCallback(() => {
        if (!ready) return;
        sendPageImpressionEvent({
            eventFn: impressionEvent,
            tableContext,
            restaurantName: data.restaurantName,
            isUserHost,
            config,
        });
    }, [ready, impressionEvent, tableContext, data.restaurantName, isUserHost, config]);

    const processData = useCallback(async () => {
        const isCurtainRaiserShown = await isFrequencyCappedWithTableId(
            STORAGE_KEYS.CURTAIN_RAISER,
            tableId as string,
            CURTAIN_RAISER_FREQUENCY_FLAG_TIMEOFFSET_MS,
        );

        if (isCurtainRaiserShown) {
            hasProcessedRef.current = true;
            setIsCurtainRaiserShown(true);
            return;
        }

        hasProcessedRef.current = true;

        if (hasPrefetchError) {
            setShowError(true);
            return;
        }

        const cards = prefetchedData?.success?.cards;
        if (cards) {
            previousOverflowRef.current = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            overflowModifiedRef.current = true;

            setData(getCurtainRaiserProps(cards));
            setReady(true);
            setTimeout(() => setShow(true), 500);
        } else {
            setShowError(true);
        }
    }, [setShow, setIsCurtainRaiserShown, tableId, hasPrefetchError, prefetchedData, setReady]);

    const handleRetry = useCallback(() => {
        window.location.reload();
    }, []);

    useEffect(() => {
        if (!tableId || !restaurantId || hasProcessedRef.current) return;
        if (isPrefetchLoading) return; // Wait for prefetch

        processData();

        return () => {
            if (overflowModifiedRef.current) {
                document.body.style.overflow = previousOverflowRef.current || "";
            }
        };
    }, [processData, isPrefetchLoading, restaurantId, tableId, hasProcessedRef]);

    if (showError) {
        setHasBottomBar?.(false);
        return <CurtainError onCtaClick={handleRetry} />;
    }

    if (!ready) {
        return null;
    }

    return (
        <CurtainRaiserContainer show={show}>
            <ElementObserver onViewEnter={onPageImpression} senseOnce>
                <CurtainRaiserUI {...data} ctaText="Swipe up to view menu" onSwipe={handleSwipeUp} swipe={swipeUp} />
            </ElementObserver>
        </CurtainRaiserContainer>
    );
};

export default memo(CurtainRaiser);
